import classNames from "classnames";
import Flickity from "flickity-imagesloaded";
import React from "react";
import Modal from "react-modal";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { FinancingModalTrigger } from "../../../apps/financing/FinancingModalTrigger";
import { RichText } from "../../../common/RichText";
import { useMountEffect } from "../../../hooks/useMountEffect";
import { IWebPageURL } from "../../../models/nominals";
import { IServiceFooterValueProps } from "../../../models/value-props";
import { isMobile } from "../../../utils/detectMobile";
import { getFooterSettings } from "../../../utils/settings";
import { ContactUsModal } from "./ContactUsModal";
import { IEmailModalSnippet } from "./EmailCaptureContent";
import { EmailModal } from "./EmailModal";
import { ServiceFooterValuePropsModal } from "./ServiceFooterValuePropsModal";

import styles from "./ServiceFooter.module.scss";

Modal.defaultStyles = {
    overlay: {},
    content: {},
};

interface IServiceFooterSnippetProps {
    title: string;
    type: string;
    icon?: string;
    content: IServiceFooterValueProps | IEmailModalSnippet | null;
    trigger: string;
    trigger_mobile: string;
}

const initCarousel = (
    carousel: HTMLDivElement,
    interval: number | null,
    loops: number | null,
    snippetsLength: number,
    carouselFlickityRef: React.MutableRefObject<Flickity | null>,
    carouselTimerRef: React.MutableRefObject<number | null>,
): void => {
    if (carousel.childNodes.length > 1) {
        carouselFlickityRef.current = new Flickity(carousel, {
            cellAlign: "center",
            pageDots: false,
            prevNextButtons: true,
            imagesLoaded: true,
            arrowShape: {
                x0: 20,
                x1: 60,
                y1: 50,
                x2: 60,
                y2: 30,
                x3: 35,
            },
            autoPlay: interval ? interval : true,
            pauseAutoPlayOnHover: true,
            wrapAround: true,
        });

        if (loops) {
            const window = getWindow();
            const carouselInterval = interval ? interval : 3000;
            const slideNum = snippetsLength;
            const duration = carouselInterval * slideNum * loops;

            if (window) {
                carouselTimerRef.current = window.setInterval(() => {
                    carouselFlickityRef.current?.stopPlayer();
                }, duration);
            }
        }

        carousel.addEventListener("mouseover", () => {
            carouselFlickityRef.current?.stopPlayer();
        });
        carousel.addEventListener("focus", () => {
            carouselFlickityRef.current?.stopPlayer();
        });
    }
};

const renderSnippetContent = (
    trigger: IServiceFooterSnippetProps,
    icon?: string,
): React.JSX.Element => {
    const content =
        isMobile.any() && trigger.trigger_mobile.length > 0
            ? trigger.trigger_mobile
            : trigger.trigger;

    return (
        <>
            {icon && <img alt="" src={icon} />}
            <RichText inline={true} html={content} />
        </>
    );
};

const renderSnippetButton = (
    isCarousel: boolean,
    snippet: IServiceFooterSnippetProps,
    onClick?: React.MouseEventHandler<HTMLButtonElement>,
): React.JSX.Element => {
    const classes = classNames({
        [styles.trigger]: true,
        [styles.carouselTrigger]: isCarousel,
    });

    return (
        <button className={classes} onClick={onClick ? onClick : undefined}>
            {renderSnippetContent(snippet, snippet.icon)}
        </button>
    );
};

export const ServiceFooter = (props: {
    interval: number | null;
    loops: number | null;
    snippets: IServiceFooterSnippetProps[];
    chatOfflineLink?: IWebPageURL;
}): React.ReactNode => {
    const carouselFlickityRef = React.useRef<Flickity | null>(null);
    const snippetsElemRef = React.useRef<HTMLDivElement | null>(null);
    const carouselTimerRef = React.useRef<number | null>(null);

    const [modalContactIsOpen, setModalContactIsOpen] = React.useState(false);
    const [modalPropsIsOpen, setModalPropsIsOpen] = React.useState(false);
    const [modalEmailIsOpen, setModalEmailIsOpen] = React.useState(false);

    useMountEffect(() => {
        if (!snippetsElemRef.current) {
            return;
        }

        initCarousel(
            snippetsElemRef.current,
            props.interval,
            props.loops,
            props.snippets.length,
            carouselFlickityRef,
            carouselTimerRef,
        );

        return () => {
            const window = getWindow();
            if (carouselFlickityRef.current) {
                if (carouselTimerRef.current && window) {
                    window.clearTimeout(carouselTimerRef.current);
                }

                carouselFlickityRef.current.destroy();
            }
        };
    });

    const onOpenModalContact = (event: React.MouseEvent<HTMLElement>): void => {
        if (event) {
            event.preventDefault();
        }

        setModalContactIsOpen(true);
    };

    const onOpenModalProps = (event: React.MouseEvent<HTMLElement>): void => {
        if (event) {
            event.preventDefault();
        }

        setModalPropsIsOpen(true);
    };

    const onOpenModalEmail = (event: React.MouseEvent<HTMLElement>): void => {
        if (event) {
            event.preventDefault();
        }

        setModalEmailIsOpen(true);
    };

    const onCloseModalContact = (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        setModalContactIsOpen(false);
    };

    const onCloseModalProps = (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        setModalPropsIsOpen(false);
    };

    const onCloseModalEmail = (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        setModalEmailIsOpen(false);
    };

    const renderSnippets = (): React.JSX.Element[] => {
        const isCarousel = props.snippets.length > 1;

        return props.snippets.map((snippet, i) => {
            switch (snippet.type) {
                case "default":
                    return (
                        <React.Fragment key={i}>
                            {renderSnippetButton(isCarousel, snippet)}
                        </React.Fragment>
                    );
                case "email":
                    return (
                        <React.Fragment key={i}>
                            {renderSnippetButton(
                                isCarousel,
                                snippet,
                                onOpenModalEmail,
                            )}
                            <EmailModal
                                isOpen={modalEmailIsOpen}
                                onClose={onCloseModalEmail}
                                content={snippet.content as IEmailModalSnippet}
                            />
                        </React.Fragment>
                    );
                case "prequal":
                    const classes = classNames({
                        [styles.financingModalTrigger]: true,
                        "al-service-footer-finance-block__pre-qualify": true,
                        [styles.carouselTrigger]: isCarousel,
                    });
                    return (
                        <FinancingModalTrigger
                            key={`Trigger-${i}`}
                            modalType="prequal-app"
                            className={classes}
                        >
                            {renderSnippetContent(snippet, snippet.icon)}
                        </FinancingModalTrigger>
                    );
                case "value_prop":
                    return (
                        <React.Fragment key={i}>
                            {renderSnippetButton(
                                isCarousel,
                                snippet,
                                onOpenModalProps,
                            )}
                            <ServiceFooterValuePropsModal
                                isOpen={modalPropsIsOpen}
                                onClose={onCloseModalProps}
                                content={
                                    snippet.content as IServiceFooterValueProps
                                }
                            />
                        </React.Fragment>
                    );
                default:
                    // TODO(i18n): Use t tagged template for error message: t`Unexpected service footer snippet type: ${snippet.type}`
                    throw new Error(
                        `Unexpected service footer snippet type: ${snippet.type}`,
                    );
            }
        });
    };

    const contactUs = !!getFooterSettings() && (
        <div
            className={`${styles.contactUs} service-footer__contact-us al-service-footer__buttons`}
        >
            <button onClick={onOpenModalContact}>{t`Contact Us`}</button>
        </div>
    );

    return (
        <div className={`${styles.root} service-footer al-service-footer`}>
            <div className={`${styles.content} l-capped-width`}>
                <div
                    className={styles.snippets}
                    ref={(ref) => {
                        snippetsElemRef.current = ref;
                    }}
                >
                    {renderSnippets()}
                </div>
                {contactUs}
            </div>
            <ContactUsModal
                isOpen={modalContactIsOpen}
                onClose={onCloseModalContact}
                chatOfflineLink={props.chatOfflineLink}
            />
            <div className={styles.iphoneXbg}></div>
        </div>
    );
};
